import React, { useState, useRef, useEffect } from 'react';
import {
  Modal, Button, Select, Tooltip, Popover, Input, Space, Divider,
} from 'antd';
import {
  Bold, Italic, Underline, Type, Image, Save, Eye, EyeOff, Plus,
} from 'react-feather';
import { DEFAULT_TEMPLATE } from './certificateTemplate';
import {
  MERGE_FIELDS,
  textToEditableHtml,
  serializeEditable,
  insertMergeFieldAtCursor,
} from './mergeFieldUtils';
import './templateEditor.css';

const EditableLine = ({
  value,
  onChange,
  className,
  allowMergeFields = false,
  previewMode,
  onFocus,
  tag: TagName = 'div',
}) => {
  const ref = useRef(null);
  const isFocused = useRef(false);

  useEffect(() => {
    if (ref.current && !isFocused.current) {
      ref.current.innerHTML = textToEditableHtml(value, previewMode, allowMergeFields);
    }
  }, [value, previewMode, allowMergeFields]);

  const handleInput = () => {
    if (ref.current) onChange(serializeEditable(ref.current));
  };

  return (
    <TagName
      ref={ref}
      className={`cierha-wysiwyg-editable ${className || ''}`}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      onFocus={() => {
        isFocused.current = true;
        onFocus?.(ref);
      }}
      onBlur={() => { isFocused.current = false; }}
    />
  );
};

const TemplateEditorModal = ({
  open,
  onClose,
  onSave,
  template,
  defaultLogos,
}) => {
  const [draft, setDraft] = useState(DEFAULT_TEMPLATE);
  const [previewMode, setPreviewMode] = useState(true);
  const [activeEditorRef, setActiveEditorRef] = useState(null);
  const [logoPopover, setLogoPopover] = useState(null);

  useEffect(() => {
    if (open) {
      setDraft({ ...DEFAULT_TEMPLATE, ...template });
    }
  }, [open, template]);

  const update = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const execFormat = (command) => {
    document.execCommand(command, false, null);
    activeEditorRef?.current?.focus();
  };

  const handleInsertField = (key) => {
    if (activeEditorRef?.current) {
      activeEditorRef.current.focus();
      insertMergeFieldAtCursor(key);
      const event = new Event('input', { bubbles: true });
      activeEditorRef.current.dispatchEvent(event);
    }
  };

  const handleSave = () => {
    onSave({
      ...draft,
      mentionOptions: draft.mentionOptions?.length
        ? draft.mentionOptions
        : DEFAULT_TEMPLATE.mentionOptions,
    });
  };

  const leftLogo = draft.leftLogoUrl || defaultLogos.left;
  const rightLogo = draft.rightLogoUrl || defaultLogos.right;

  const logoPopoverContent = (side) => (
    <div className="cierha-logo-popover">
      <p>URL du logo {side === 'left' ? 'gauche' : 'droit'}</p>
      <Input
        placeholder="https://... (vide = logo par défaut)"
        value={side === 'left' ? draft.leftLogoUrl : draft.rightLogoUrl}
        onChange={(e) => update(side === 'left' ? 'leftLogoUrl' : 'rightLogoUrl', e.target.value)}
      />
    </div>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="98vw"
      style={{ top: 8, paddingBottom: 0 }}
      className="cierha-word-modal"
      title={null}
      footer={null}
      destroyOnClose
      closable={false}
    >
      <div className="cierha-word-app">
        {/* Title bar */}
        <div className="cierha-word-titlebar">
          <span className="cierha-word-docname">Modèle de certificat — CIERHA</span>
          <button type="button" className="cierha-word-close" onClick={onClose}>×</button>
        </div>

        {/* Ribbon */}
        <div className="cierha-word-ribbon">
          <div className="cierha-ribbon-group">
            <span className="cierha-ribbon-label">Police</span>
            <Tooltip title="Gras">
              <button type="button" className="cierha-ribbon-btn" onClick={() => execFormat('bold')}>
                <Bold size={15} />
              </button>
            </Tooltip>
            <Tooltip title="Italique">
              <button type="button" className="cierha-ribbon-btn" onClick={() => execFormat('italic')}>
                <Italic size={15} />
              </button>
            </Tooltip>
            <Tooltip title="Souligné">
              <button type="button" className="cierha-ribbon-btn" onClick={() => execFormat('underline')}>
                <Underline size={15} />
              </button>
            </Tooltip>
          </div>

          <div className="cierha-ribbon-divider" />

          <div className="cierha-ribbon-group">
            <span className="cierha-ribbon-label">Insérer</span>
            <Select
              className="cierha-field-select"
              placeholder="Champ de fusion"
              style={{ width: 200 }}
              onChange={handleInsertField}
              value={null}
              suffixIcon={<Plus size={14} />}
              options={MERGE_FIELDS.map((f) => ({
                value: f.key,
                label: f.label,
              }))}
            />
            <Tooltip title={previewMode ? 'Afficher les codes {{champ}}' : 'Aperçu avec données exemple'}>
              <button
                type="button"
                className={`cierha-ribbon-btn ${previewMode ? 'active' : ''}`}
                onClick={() => setPreviewMode((p) => !p)}
              >
                {previewMode ? <Eye size={15} /> : <EyeOff size={15} />}
              </button>
            </Tooltip>
          </div>

          <div className="cierha-ribbon-spacer" />

          <Space>
            <Button onClick={onClose}>Annuler</Button>
            <Button type="primary" icon={<Save size={15} />} onClick={handleSave}>
              Enregistrer le modèle
            </Button>
          </Space>
        </div>

        {/* Workspace */}
        <div className="cierha-word-workspace">
          {/* Page */}
          <div className="cierha-word-page-area">
            <div className="cierha-word-page">
              {/* Header */}
              <div className="cierha-wysiwyg-header">
                <Popover
                  content={logoPopoverContent('left')}
                  title="Logo gauche"
                  trigger="click"
                  open={logoPopover === 'left'}
                  onOpenChange={(v) => setLogoPopover(v ? 'left' : null)}
                >
                  <div className="cierha-wysiwyg-logo-wrap">
                    <img src={leftLogo} alt="Logo gauche" className="cierha-wysiwyg-logo cierha-wysiwyg-logo-left" />
                    <div className="cierha-wysiwyg-logo-overlay"><Image size={14} /> Modifier</div>
                  </div>
                </Popover>

                <div className="cierha-wysiwyg-header-center">
                  <EditableLine
                    value={draft.universityName}
                    onChange={(v) => update('universityName', v)}
                    className="cierha-wysiwyg-university"
                    onFocus={setActiveEditorRef}
                    tag="h1"
                  />
                  <EditableLine
                    value={draft.chairName}
                    onChange={(v) => update('chairName', v)}
                    className="cierha-wysiwyg-chair"
                    onFocus={setActiveEditorRef}
                    tag="h2"
                  />
                  <EditableLine
                    value={draft.acronym}
                    onChange={(v) => update('acronym', v)}
                    className="cierha-wysiwyg-acronym"
                    onFocus={setActiveEditorRef}
                    tag="h3"
                  />
                  <EditableLine
                    value={draft.address}
                    onChange={(v) => update('address', v)}
                    className="cierha-wysiwyg-address"
                    onFocus={setActiveEditorRef}
                  />
                  <div className="cierha-wysiwyg-contact">
                    Web:{' '}
                    <EditableLine
                      value={draft.web}
                      onChange={(v) => update('web', v)}
                      className="cierha-wysiwyg-inline"
                      onFocus={setActiveEditorRef}
                      tag="span"
                    />
                    ; Email:{' '}
                    <EditableLine
                      value={draft.email}
                      onChange={(v) => update('email', v)}
                      className="cierha-wysiwyg-inline"
                      onFocus={setActiveEditorRef}
                      tag="span"
                    />
                  </div>
                </div>

                <Popover
                  content={logoPopoverContent('right')}
                  title="Logo droit"
                  trigger="click"
                  open={logoPopover === 'right'}
                  onOpenChange={(v) => setLogoPopover(v ? 'right' : null)}
                >
                  <div className="cierha-wysiwyg-logo-wrap">
                    <img src={rightLogo} alt="Logo droit" className="cierha-wysiwyg-logo cierha-wysiwyg-logo-right" />
                    <div className="cierha-wysiwyg-logo-overlay"><Image size={14} /> Modifier</div>
                  </div>
                </Popover>
              </div>

              <div className="cierha-wysiwyg-separator" />

              <div className="cierha-wysiwyg-title-row">
                <EditableLine
                  value={draft.documentTitle}
                  onChange={(v) => update('documentTitle', v)}
                  className="cierha-wysiwyg-doc-title"
                  onFocus={setActiveEditorRef}
                  tag="h1"
                />
                <span className="cierha-wysiwyg-ref-hint">
                  N° <span className="cierha-merge-field cierha-merge-field-static" contentEditable={false}>{'{{numeroRef}}'}</span>
                </span>
              </div>

              <EditableLine
                value={draft.clause1}
                onChange={(v) => update('clause1', v)}
                className="cierha-wysiwyg-paragraph"
                onFocus={setActiveEditorRef}
                tag="p"
              />
              <EditableLine
                value={draft.clause2}
                onChange={(v) => update('clause2', v)}
                className="cierha-wysiwyg-paragraph"
                onFocus={setActiveEditorRef}
                tag="p"
              />
              <EditableLine
                value={draft.clause3}
                onChange={(v) => update('clause3', v)}
                className="cierha-wysiwyg-paragraph"
                onFocus={setActiveEditorRef}
                tag="p"
              />

              <EditableLine
                value={draft.awardTemplate}
                onChange={(v) => update('awardTemplate', v)}
                className="cierha-wysiwyg-paragraph cierha-wysiwyg-award"
                allowMergeFields
                previewMode={previewMode}
                onFocus={setActiveEditorRef}
                tag="p"
              />

              <EditableLine
                value={draft.dateLineTemplate}
                onChange={(v) => update('dateLineTemplate', v)}
                className="cierha-wysiwyg-date"
                allowMergeFields
                previewMode={previewMode}
                onFocus={setActiveEditorRef}
                tag="p"
              />

              <div className="cierha-wysiwyg-signatures">
                <div className="cierha-wysiwyg-sig-block">
                  <EditableLine
                    value={draft.signatureLeftName}
                    onChange={(v) => update('signatureLeftName', v)}
                    className="cierha-wysiwyg-sig-name"
                    onFocus={setActiveEditorRef}
                    tag="p"
                  />
                  <EditableLine
                    value={draft.signatureLeftTitle}
                    onChange={(v) => update('signatureLeftTitle', v)}
                    className="cierha-wysiwyg-sig-title"
                    onFocus={setActiveEditorRef}
                    tag="p"
                  />
                </div>
                <div className="cierha-wysiwyg-sig-block cierha-wysiwyg-sig-right">
                  <EditableLine
                    value={draft.signatureRightName}
                    onChange={(v) => update('signatureRightName', v)}
                    className="cierha-wysiwyg-sig-name"
                    onFocus={setActiveEditorRef}
                    tag="p"
                  />
                  <EditableLine
                    value={draft.signatureRightTitle}
                    onChange={(v) => update('signatureRightTitle', v)}
                    className="cierha-wysiwyg-sig-title"
                    onFocus={setActiveEditorRef}
                    tag="p"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Fields panel */}
          <aside className="cierha-word-sidebar">
            <div className="cierha-sidebar-section">
              <h4><Type size={14} /> Champs de fusion</h4>
              <p className="cierha-sidebar-hint">
                Cliquez pour insérer dans le paragraphe actif. Les champs bleus se remplissent automatiquement à la génération.
              </p>
              <div className="cierha-field-list">
                {MERGE_FIELDS.map((field) => (
                  <button
                    key={field.key}
                    type="button"
                    className="cierha-field-chip"
                    onClick={() => handleInsertField(field.key)}
                  >
                    <code>{`{{${field.key}}}`}</code>
                    <span>{field.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <Divider />

            <div className="cierha-sidebar-section">
              <h4>Options de mention</h4>
              <p className="cierha-sidebar-hint">Utilisées dans le menu déroulant du formulaire.</p>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                placeholder="Ajouter une mention"
                value={draft.mentionOptions}
                onChange={(vals) => update('mentionOptions', vals)}
                tokenSeparators={[',']}
              />
            </div>
          </aside>
        </div>
      </div>
    </Modal>
  );
};

export default TemplateEditorModal;
